/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 10 2024
 **/

import { useAtomValue } from "jotai";
import { useEffect } from "react";

import { EventHandler } from "@rfice/eventhandler";

import { useMiniChatPopupManager, useMinimizeChatList, usePopover } from "@/frameworks/core/hooks";
import { GROUP_POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { SwitchCase } from "@/frameworks/libraries/components";
import { Menu } from "@/frameworks/designStuff/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { usePersistActions } from "@/frameworks/core/persistence";

import TopBarColor, { topBarColorAtomFamily } from "./TopBarColor";
import { SpaceChannelOption } from "../../FullSizeChat/space/components";
import { DMChatOptionList } from "../../../DMChatOptionList";
import { OpenWorldChatOptionList } from "../../../OpenWorldChatOptionList";

export default function useMiniChatPopup({
	id,
	spaceID,
	groupID,
	threadID
}: {
	id: string;
	spaceID?: string;
	groupID?: string;
	threadID?: string;
}) {
	const topBarColor = useAtomValue(topBarColorAtomFamily(id || ""));
	const { minimizeChatListActions } = useMinimizeChatList();
	const { miniChatPopupActions } = useMiniChatPopupManager();
	const { users, chats } = usePersistActions();

	const { toggle } = usePopover();

	const handleChannelOption = (
		ev: React.MouseEvent,
		channelData: WebServices.SpaceChat.List.Data[number]["rooms"][number]
	) => {
		const { adminCount, isCurrentUserAdmin } = channelData.users.reduce(
			(acc, user) => {
				if (user.spaceAuthority === "SPACE_ADMIN") {
					acc.adminCount++;
					if (user.id === Configure.userID) {
						acc.isCurrentUserAdmin = true;
					}
				}
				return acc;
			},
			{ adminCount: 0, isCurrentUserAdmin: false }
		);

		toggle({
			popoverID: GROUP_POPOVER_ID.CHAT_OPTION,
			uniqueID: id,
			content: (
				<SwitchCase
					condition={channelData.roomType}
					cases={{
						OPEN_WORLD: (
							<Menu.Group css={{ overflow: "hidden" }}>
								<OpenWorldChatOptionList groupID={groupID || ""} spaceID={channelData.spaceId || ""} />
								<Menu fullWidth css={{ borderRadius: 0 }}>
									<TopBarColor spaceID={channelData.roomId || ""} />
								</Menu>
							</Menu.Group>
						),
						CHANNEL: (
							<Menu.Group css={{ overflow: "hidden" }}>
								<SpaceChannelOption
									variant="miniPopup"
									spaceID={channelData.spaceId || ""}
									isOnlyAdmin={adminCount === 1 && isCurrentUserAdmin}
									notificationType={channelData.notificationType}
									roomID={channelData.roomId}
									roomType={
										channelData.general ? "general" : channelData.private ? "private" : "public"
									}
									users={channelData.users}
								/>
								<Menu fullWidth css={{ borderRadius: 0 }}>
									<TopBarColor spaceID={channelData.roomId || ""} />
								</Menu>
							</Menu.Group>
						)
					}}
					defaultCase={<DMChatOptionList channelID={channelData.roomId} />}
				/>
			),
			anchorEl: ev.currentTarget,
			direction: {
				x: "left",
				y: "bottom"
			},
			zIndex: ZIndex.POPUP
		});
	};

	const handleClose = () => {
		EventHandler.fire("$Editor::Draft$", id);

		minimizeChatListActions.remove(id, threadID);
		const me = users.get_user(Configure.userID);
		const ignoreUpdate = Boolean(me?.currentSpace || me?.currentConference);
		miniChatPopupActions.hide(id, ignoreUpdate);
		const draft = chats.chat_draft(id);
		if (draft) {
			chats.check_has_draft(id, true);
		}
	};

	const handleHide = (
		type: "DM" | "Space",
		{
			users,
			spaceName
		}: {
			users?: {
				src?: string | undefined;
				name: string;
				userID: string;
			}[];
			spaceName?: string;
		},
		size?: {
			width?: number;
			height?: number;
		}
	) => {
		EventHandler.fire("$Editor::Draft$", id);
		if (type === "DM") {
			minimizeChatListActions.append({ type, users: users ?? [], id, size });
		}
		if (type === "Space") {
			minimizeChatListActions.append({ type, spaceID: spaceID ?? "", spaceName: spaceName ?? "", id, size });
		}

		miniChatPopupActions.hide(id);
	};

	useEffect(() => {
		minimizeChatListActions.remove(id);
	}, []);

	return { handleClose, handleHide, handleChannelOption, topBarColor };
}
